import { useContext, useEffect, useReducer } from 'react'
import {
    Grid,
    UniversalLoadingCircle,
    UniversalModal,
    UniversalToolBar,
    UniversalDataTable,
} from '../../../UniversalComponents'
import {
    Archive as ArchiveIcon,
    Restore,
    ContentCopy,
    SensorOccupied as SensorOccupiedIcon,
} from '@mui/icons-material'
import {
    getSignalProfiles,
    activateSignalProfiles,
    deleteSignalProfiles,
    cloneSignalProfiles,
} from '../../../../apiRoutes'
import {
    usePermissions,
    Authentication,
    useAuth,
} from '../../../../context/AuthenticationContext'
import {
    handleActivation,
    handleDeletion,
    handleClone,
    standardColumnMapper,
    permissionsCheck,
} from '../../../../utilities/functionTools'
import { useMinimizer } from '../../../../context/MinimizeContext'
import { useSocket } from '../../../../context/SocketContext'
import SignalProfileManagerForm from './SignalProfileManagerForm'
import { ToastMessage } from '../../../../context/ToastPopUpContext'

const today = new Date().toISOString().substring(0, 10)

const SignalProfileManager = ({ restoredData }) => {
    const { apiSocket } = useSocket()

    const currentUser = useContext(Authentication)
    const { username, userProfile, activeManufacturer, userPreferences } =
        currentUser.auth
    const { setUser } = useAuth()
    const { superAdmin, userProfilePermissions } = userProfile

    const reducer = (state, newState) => ({ ...state, ...newState })
    const defaultState = {
        open: restoredData.id === undefined ? false : true,
        row: restoredData.id === undefined ? {} : restoredData,
        formCache: {},
        data: [],
        viewDeactivated: false,
        activateData: [],
        signals: {
            signalProfileManager: {
                columns: [
                    {
                        field: 'profileName',
                        headerName: 'PROFILE NAME',
                        width: 150,
                        type: 'string',
                    },
                    {
                        field: 'manufacturerName',
                        headerName: 'MANUFACTURER',
                        width: 150,
                        type: 'string',
                    },
                ],
                density: 'standard',
            },
        },
        density:
            userPreferences?.dataTableColumns?.signals?.signalProfileManager
                ?.density || 'standard',
    }

    const [state, setState] = useReducer(reducer, defaultState)
    const setToastMessage = useContext(ToastMessage)

    const {
        open,
        row,
        formCache,
        data,
        viewDeactivated,
        activateData,
        signals,
        density,
    } = state

    const { signalProfileManager } = signals

    const { setMinimized } = useMinimizer()

    const getData = async (active: boolean) => {
        try {
            const getSignalProfilesData = await getSignalProfiles(active)
            if (getSignalProfilesData && getSignalProfilesData.length !== 0) {
                const signalProfiles = standardColumnMapper(
                    defaultColumns,
                    getSignalProfilesData
                )

                setState({
                    data: signalProfiles,
                })
            } else {
                setState({
                    data: [],
                })
            }
        } catch (err: any) {
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    const collectColumnData = () => {
        const data =
            userPreferences?.dataTableColumns?.signals?.signalProfileManager
                ?.columns

        if (Array.isArray(data)) {
            const mapColumns = data.map((item) => ({
                field: item?.field,
                headerName: item?.headerName,
                width: item?.width,
                isVisible: item.isVisible,
                type: item?.type,
            }))

            const format = {
                signalProfileManager: {
                    columns: mapColumns,
                },
            }

            setState({ ...state, signals: format })
        }
    }

    const submitColumnData = async () => {
        try {
            setUser({
                key: 'signals',
                value: signals,
                type: 'columnData',
            })
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleActivateData = () => {
        handleActivation({
            activateData,
            data,
            returnKey: 'profileName',
            setState,
            getData,
            route: activateSignalProfiles,
            viewDeactivated,
            setToastMessage,
            page: 'profiles',
        })
    }

    const handleDeletionData = () => {
        handleDeletion({
            activateData,
            data,
            returnKey: 'profileName',
            setState,
            getData,
            route: deleteSignalProfiles,
            viewDeactivated,
            setToastMessage,
            page: 'profiles',
        })
    }

    const handleCloneData = () => {
        handleClone({
            cloneData: activateData,
            data,
            returnKey: 'profileName',
            setState,
            getData,
            route: cloneSignalProfiles,
            viewDeactivated,
            setToastMessage,
            page: 'profiles',
        })
    }

    const handleDeletionCheck = () => {
        setToastMessage(
            `Are you sure you want to delete these signal profiles?`,
            'warning',
            handleDeletionData
        )
    }

    const handleCloneCheck = () => {
        if (activateData && activateData.length !== 0) {
            setToastMessage(
                `Are you sure you want to clone these signal profiles?`,
                'warning',
                handleCloneData
            )
        } else {
            setToastMessage(
                `Please select profiles to clone from table.`,
                'error'
            )
        }
    }

    useEffect(() => {
        collectColumnData()
        getData(true)
        apiSocket?.on('signal-profiles', (newData) => {
            if (newData) {
                getData(!viewDeactivated)
                setToastMessage(`Signal profiles have just updated.`)
            }
        })
        setMinimized(false, restoredData.id, true)
    }, [])

    useEffect(() => {
        getData(!viewDeactivated)
    }, [viewDeactivated, activeManufacturer])

    const minimizeModal = () => {
        setMinimized(true, {
            title: formCache.signalFriendlyName
                ? `Edit: ${formCache.signalFriendlyName}`
                : 'Add New Signal Profile',
            link: '/data-hub/signals',
            data: formCache.id
                ? { ...formCache, tab: 'signal-profiles' }
                : { ...formCache, id: 'newSignal', tab: 'signal-profiles' },
            id: formCache.id ? formCache.id : 'newSignal',
        })
        setState({ open: false })
    }

    const setModalVisbility = async (tableRow: any, newData: any) => {
        if (tableRow?.profileName) {
            setState({ row: tableRow, open: !open })
        } else {
            setState({ row: defaultState.row, open: !open })
        }
        if (newData !== undefined) {
            return getData(!viewDeactivated)
        }
    }

    const setViewDeactivated = () =>
        setState({ viewDeactivated: !viewDeactivated })

    const buttons = [
        {
            buttonName: 'Add New Signal Profile',
            buttonIcon: <SensorOccupiedIcon />,
            buttonFunction: () => setState({ open: !open }),
            disabled: permissionsCheck(userProfilePermissions, 'Signals'),
        },
        {
            buttonName: 'Clone Signal Profiles',
            buttonIcon: <ContentCopy />,
            buttonFunction: () => handleCloneCheck(),
            disabled: permissionsCheck(userProfilePermissions, 'Signals'),
        },
        {
            buttonName: !viewDeactivated ? 'Deactivate' : 'Activate',
            buttonIcon: <ArchiveIcon />,
            buttonFunction: handleActivateData,
            disabled: permissionsCheck(userProfilePermissions, 'Signals'),
        },
        {
            buttonName: !viewDeactivated
                ? 'View Deactivated Signal Profiles'
                : 'View Active Signal Profiles',
            buttonIcon: <Restore />,
            buttonFunction: setViewDeactivated,
        },
    ]

    const defaultColumns = ['profileName', 'manufacturer.manufacturerName']

    const visibleColumns = ['profileName', 'manufacturerName']

    const customColumnNames = ['Profile Name', 'Manufacturer']

    const handleUpdatedColumnsChange = (updatedColumns) => {
        const format = updatedColumns.map((col) => ({
            field: col.field,
            headerName: col.headerName,
            width: col.width,
            isVisible: col.isVisible,
            type: col.type,
        }))

        const setDensity = state.density

        const setData = {
            signalProfileManager: { columns: format, density: setDensity },
        }

        setState({
            signals: setData,
        })
    }

    const handleDensityChange = (newDensity) => {
        setState({
            density: newDensity,
            signals: {
                signalProfileManager: {
                    ...signalProfileManager,
                    density: newDensity,
                },
            },
        })
    }

    return (
        <Grid container>
            <Grid container>
                <UniversalToolBar buttons={buttons} />
            </Grid>

            <Grid item xs={12}>
                <UniversalLoadingCircle
                    data={data}
                    reloadFunction={
                        !viewDeactivated
                            ? () => getData(true)
                            : () => getData(false)
                    }
                    customTimeoutMessage={
                        !viewDeactivated
                            ? 'No Active Signal Profiles Available'
                            : 'No Deactivated Signal Profiles Available'
                    }
                >
                    <UniversalDataTable
                        data={data}
                        apiDensity={density}
                        apiColumns={signals?.signalProfileManager?.columns}
                        savePreferences={() => submitColumnData()}
                        visibleColumns={[]}
                        customColumnNames={customColumnNames}
                        getRowData={(tableRow) =>
                            setModalVisbility(tableRow, undefined)
                        }
                        onRowSelectionModelChange={(rowSelection) => {
                            setState({ activateData: rowSelection })
                        }}
                        onUpdatedColumnsChange={handleUpdatedColumnsChange}
                        onDensityChange={handleDensityChange}
                    />
                </UniversalLoadingCircle>
            </Grid>

            <UniversalModal
                title={`${
                    row.signalName
                        ? ` ${row.signalFriendlyName}`
                        : 'Signal Profile Manager'
                }`}
                timeStamp={`${
                    row.updatedBy
                        ? ` ${
                              row.updatedBy
                                  ? ` Modified By ${row.updatedBy}`
                                  : ''
                          } ${
                              row.updatedAt
                                  ? `| Modified Date ${row.updatedAt.substring(
                                        0,
                                        10
                                    )}`
                                  : ''
                          }  `
                        : ` Modified By: ${username} | Modified Date: ${today} `
                }`}
                open={open}
                onClose={(tableRow) => setModalVisbility(tableRow, undefined)}
                wrapperStyle={{
                    margin: 'auto',
                    width: '90%',
                    marginTop: 5,
                }}
                onMinimize={minimizeModal}
                draggable={true}
                minimizable={true}
            >
                <SignalProfileManagerForm
                    row={row}
                    formDescription={''}
                    onClose={(tableRow: any, newData: any) =>
                        setModalVisbility(tableRow, newData)
                    }
                    saveCache={(data: any) => setState({ formCache: data })}
                    permissionCheck={permissionsCheck(
                        userProfilePermissions,
                        'Signals'
                    )}
                />
            </UniversalModal>
        </Grid>
    )
}

export default SignalProfileManager
